import { inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TradingControllerService } from '@api/client';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withFeature,
  withMethods,
  withProps,
} from '@ngrx/signals';
import { SharedStore } from '@shared/store/shared.signal-store';
import { DateTimeService } from '@shared/services/datetime/datetime.service';
import { DownloadStateService } from '@shared/services/download-state.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { useActiveOrders } from './features/active-orders.feature';
import { useAssets } from './features/assets.feature';
import { useBalances } from './features/balances.feature';
import { useCustomSettings } from './features/custom-settings.feature';
import { useDeposits } from './features/deposits.feature';
import { useOrders } from './features/orders.feature';
import { usePairs } from './features/pairs.feature';
import { useReports } from './features/reports.feature';
import { useSubAccountTransfers } from './features/sub-account-transfers.feature';
import { useTrading } from './features/trading.feature';
import { useTransfers } from './features/transfers.feature';
import { useWithdrawals } from './features/withdrawals.feature';
import { useWsStatus } from './features/ws-status.feature';

/**
 * Feature order is load-bearing and checked at compile time by the `type<...>()`
 * contracts each feature declares:
 *   useAssets   -> assetScaleMap, consumed by useBalances
 *   useBalances -> allBalances + loadBalance, consumed by useTrading / useWithdrawals
 *   usePairs    -> baseAssetName / quoteAssetName / selectedPair, consumed by useTrading
 * Reordering these produces a type error, not a runtime surprise.
 */
export const DashboardStore = signalStore(
  { providedIn: 'root' },
  withProps(() => ({
    _injector: inject(Injector),
    _router: inject(Router),
    _dialogService: inject(MatDialog),
    _notificationService: inject(NotificationService),
    _dateTimeService: inject(DateTimeService),
    _downloadStateService: inject(DownloadStateService),
    _tradingService: inject(TradingControllerService),
    _activeAccountId: inject(SharedStore).activeAccountId,
  })),
  withFeature(useAssets),
  withFeature(useBalances),
  withFeature(usePairs),
  withFeature(useOrders),
  withFeature(useActiveOrders),
  withFeature(useTransfers),
  withFeature(useDeposits),
  withFeature(useWithdrawals),
  withFeature(useSubAccountTransfers),
  withFeature(useCustomSettings),
  withFeature(useTrading),
  withFeature(useReports),
  withFeature(useWsStatus),
  withMethods(store => {
    const loadInitialData = () => {
      store.loadAssets();
      store.loadBalance();
      store.loadTransfers();
      store.loadPairs();
      store.loadCustomSettings();
    };

    const resetAccountData = () =>
      patchState(store, {
        balance: undefined,
        orders: [],
        activeOrders: [],
        transfers: [],
        depositDetails: [],
        fiatDepositDetails: undefined,
        withdrawalDetails: [],
        rate: undefined,
      });

    return { loadInitialData, resetAccountData };
  }),
);
